/**
 * Soft gate for per-app pages whose content (deploys, secrets, schedules,
 * settings, role management) is only meaningful to a manager of the app.
 *
 * Non-managers who navigate directly to a manager-only URL — workspace-
 * visible apps grant viewer access to any signed-in user, so this is
 * reachable by typing the URL — see a calm "Managers only" notice with a
 * link back to the overview. Defense in depth on top of the server's
 * 403 on every mutating endpoint.
 *
 * The companion change in `AppTabs` already hides the manager-only tabs
 * for non-managers; this is what catches a bookmark / paste of the URL.
 */

import { Link } from 'react-router-dom';
import { Card, PageHeader } from './ui.js';
import { IconLock } from './Icons.js';
import { AppTabs } from './AppTabs.js';

export interface ManagerOnlyNoticeProps {
  appSlug: string;
  appName: string;
}

export function ManagerOnlyNotice({ appSlug, appName }: ManagerOnlyNoticeProps) {
  return (
    <Card>
      <div className="p-8 flex flex-col items-center text-center gap-3">
        <div
          className="w-11 h-11 rounded-xl bg-surface-2 text-muted flex items-center justify-center"
          aria-hidden
        >
          <IconLock size={20} />
        </div>
        <h2 className="font-display text-lg font-semibold tracking-tight">Managers only</h2>
        <p className="text-muted text-sm max-w-sm">
          This area is for managers of <strong>{appName}</strong>. Ask a manager to add you, or head
          back to the overview.
        </p>
        <Link
          to={`/apps/${appSlug}`}
          className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-brand hover:underline"
        >
          Back to {appName}
        </Link>
      </div>
    </Card>
  );
}

/**
 * Full-page render for a non-manager who navigated to a manager-only
 * URL. Replaces the identical PageHeader + AppTabs(false) +
 * ManagerOnlyNotice block that lived in 5 separate per-app pages.
 */
export interface ManagerOnlyPageProps {
  app: { slug: string; name: string };
  title: string;
  description: string;
}

export function ManagerOnlyPage({ app, title, description }: ManagerOnlyPageProps) {
  return (
    <>
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title={title}
        description={description}
      />
      <AppTabs slug={app.slug} isManager={false} />
      <ManagerOnlyNotice appSlug={app.slug} appName={app.name} />
    </>
  );
}
